import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import axios from 'axios';
import {
  getToken,
  setToken as saveToken,
  clearAuthData,
  getUserFromToken,
} from '../utils/auth';
import { refreshAccessToken } from '../lib/axios';
import { useTokenRefresh } from '../hooks/useTokenRefresh';
import { API_ENDPOINTS } from '../config/api.config';

interface User {
  id?: string;
  email?: string;
  name?: string;
  role?: string;
  [key: string]: unknown;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (token: string) => void;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const logout = useCallback(async () => {
    // Get current access token before clearing
    const accessToken = getToken();

    // Clear local auth data first (so UI updates immediately)
    clearAuthData();
    setUser(null);
    setIsAuthenticated(false);

    // Then try to call logout API to clear httpOnly cookie
    // and invalidate tokens on server
    if (accessToken) {
      try {
        await axios.post(
          API_ENDPOINTS.AUTH.LOGOUT,
          { accessToken },
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            withCredentials: true,
          },
        );
      } catch (error) {
        // Ignore logout errors - user is already logged out locally
        console.warn('[AuthContext] Logout API call failed:', error);
      }
    }
  }, []);

  // Initialize token refresh monitoring
  useTokenRefresh(logout);

  useEffect(() => {
    const initAuth = async () => {
      // Check if we have a token in memory
      const existingToken = getToken();

      if (existingToken) {
        // Token exists in memory, use it
        const userData = getUserFromToken();
        setUser(userData as User);
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      // No token in memory - try to restore session via refresh endpoint
      // (browser will send httpOnly cookie automatically)
      try {
        console.log(
          '[AuthContext] Attempting to restore session via refresh...',
        );
        await refreshAccessToken();

        // If successful, token is now in memory
        const userData = getUserFromToken();
        setUser(userData as User);
        setIsAuthenticated(true);
        console.log('[AuthContext] Session restored successfully');
      } catch (error) {
        // No valid refresh token cookie or it's expired
        console.log(
          '[AuthContext] Session restoration failed, user needs to login',
          error,
        );
        clearAuthData();
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback((token: string) => {
    console.log('[AuthContext] Login called', {
      hasToken: !!token,
    });

    saveToken(token);

    const userData = getUserFromToken();
    setUser(userData as User);
    setIsAuthenticated(true);
    console.log('[AuthContext] Login complete');
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
